/**
 * Flight progress along a known origin→destination route: how far along the
 * leg an aircraft is, and when it should arrive. This is the "progress bar +
 * ETA" a commercial tracker shows, computed locally from the live position
 * and the (callsign-derived) route endpoints — no schedule feed involved.
 *
 * Progress is measured along the *actual* path flown-so-far vs remaining
 * (origin→aircraft plus aircraft→destination) rather than against the direct
 * origin→destination great circle. An aircraft holding, vectored off-track or
 * diverting then reads as "less far along" instead of the fraction jumping
 * around or exceeding 1 whenever it strays off the direct line.
 */
import { distanceKm } from './map/geo';
import type { Aircraft, RouteInfo } from './types';

const KM_PER_NM = 1.852;
/** Below this ground speed an ETA is meaningless (taxiing, holding, stale fix). */
const MIN_ETA_SPEED_KT = 40;

export interface FlightProgress {
  /** 0..1 along the leg (origin → destination). */
  fraction: number;
  distanceFlownKm: number;
  distanceRemainingKm: number;
  /** Epoch ms, or null when ground speed is unknown/too low to extrapolate. */
  etaMs: number | null;
}

/** Null when the route lacks usable endpoint coordinates. */
export function flightProgress(
  ac: Pick<Aircraft, 'lat' | 'lon' | 'groundSpeedKt'>,
  route: Pick<
    RouteInfo,
    'originLat' | 'originLon' | 'destinationLat' | 'destinationLon'
  > | null,
  now = Date.now(),
): FlightProgress | null {
  if (
    !route ||
    route.originLat == null ||
    route.originLon == null ||
    route.destinationLat == null ||
    route.destinationLon == null
  ) {
    return null;
  }
  const pos: [number, number] = [ac.lon, ac.lat];
  const distanceFlownKm = distanceKm([route.originLon, route.originLat], pos);
  const distanceRemainingKm = distanceKm(pos, [route.destinationLon, route.destinationLat]);
  const total = distanceFlownKm + distanceRemainingKm;
  // Degenerate route (origin == destination, or aircraft sitting on both):
  // report "not started" rather than dividing by zero.
  const fraction = total > 0 ? distanceFlownKm / total : 0;

  const speedKt = ac.groundSpeedKt;
  const etaMs =
    speedKt != null && speedKt >= MIN_ETA_SPEED_KT
      ? now + (distanceRemainingKm / KM_PER_NM / speedKt) * 3_600_000
      : null;

  return { fraction, distanceFlownKm, distanceRemainingKm, etaMs };
}

/** "1h 24m" / "38m" — remaining time to an ETA, or null if it's in the past/unknown. */
export function formatEta(etaMs: number | null, now = Date.now()): string | null {
  if (etaMs == null) return null;
  const mins = Math.round((etaMs - now) / 60_000);
  if (mins < 0) return null;
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

/** Local clock time of an ETA, e.g. "14:32". */
export function formatEtaClock(etaMs: number | null): string | null {
  if (etaMs == null) return null;
  return new Date(etaMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
